import { Component, Inject, EventEmitter } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Observable } from 'rxjs'
import { formatDate } from '../../../../utility'

interface Data {
    travelDates: {
        checkin: string,
        checkout: string,
        nights: number
    },
    totalGuests: number,
    listingId: string,
    listingName: string,
    commissionRate: string,
    commissionAmount$: Observable<any>,
    price: string,
    priceInListingCurrency: string,
    ZARListing: Boolean,
    adjustedPrice?: {
        amountMicros: number,
        currency: string
    },
    installmentSchedule?: {
        installmentScheduleId: string,
        installments: Array<{
            installmentScheduleId: string,
            installmentId: string,
            daysPriorTo: number | string | null,
            percentage: number
        }>
    },
    consumerView: boolean
}

@Component({
    selector: 'app-booking-summary',
    templateUrl: './booking-summary.html'
})
export class BookingSummaryComponent {
    
    loadingCheckout = false
    checkin: string
    checkout: string
    $proceedToCheckout = new EventEmitter<Boolean>()

    constructor(
        private _dialogRef: MatDialogRef<BookingSummaryComponent>,
        @Inject(MAT_DIALOG_DATA) public data: Data) {

        this.checkin = formatDate(data.travelDates.checkin)
        this.checkout = formatDate(data.travelDates.checkout)
    }

    getInstallmentAmount(percentage: number): string {
        if (!this.data?.adjustedPrice) {
            console.warn('AdjustedPrice is not available for installment calculation')
            return ''
        }

        const baseAmount = this.data.adjustedPrice.amountMicros / 1_000_000
        const amount = (baseAmount * percentage) / 100

        return amount.toLocaleString('en-US', {
            style: 'currency',
            currency: this.data.adjustedPrice.currency,
            maximumFractionDigits: 0
        })
    }

    formatDaysPrior(days: number | string | null): string {
        if (!days) return 'upon confirmation'

        if (typeof days === 'string' && days.startsWith('P')) {
            const daysNum = parseInt(days.replace(/P(\d+)D/, '$1'))
            return `${daysNum} days before check-in`
        }

        const daysNum = typeof days === 'string' ? parseInt(days) : days

        if (daysNum === 0) return 'upon confirmation'
        return `${daysNum} days before check-in`
    }

    isPaymentDueNow(daysPriorTo: number | string | null): boolean {
        if (!daysPriorTo || daysPriorTo === 'P0D') return true;
        
        if (typeof daysPriorTo === 'string' && daysPriorTo.startsWith('P')) {
            const daysNum = parseInt(daysPriorTo.replace(/P(\d+)D/, '$1'));
            daysPriorTo = daysNum;
        }

        if (!this.data?.travelDates?.checkin) return false;

        const today = new Date();
        const checkin = new Date(this.data.travelDates.checkin);
        const daysToCheckin = Math.ceil((checkin.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        return daysToCheckin <= Number(daysPriorTo);
    }

    getTotalPercentageDueNow(): number {
        if (!this.data?.installmentSchedule?.installments) return 0;
        
        return this.data.installmentSchedule.installments
            .filter(installment => this.isPaymentDueNow(installment.daysPriorTo))
            .reduce((sum, installment) => sum + installment.percentage, 0);
    }

    confirm() {
        this.setLoadingState(true)
        this.$proceedToCheckout.emit(true)
    }

    decline() {
        this._dialogRef.close(false)
    }

    setLoadingState(value: boolean) {
        this.loadingCheckout = value
    }

    readonly Number = Number
}
